import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft } from "lucide-react";
import { AuthContext } from "../../../context/authContext";
import {
  makePayPalPayment,
  updateGiftCardRedemptionDetails,
} from "../../../reducers/payouts.slice";
import {
  downloadWithdrawalInvoice,
  updateWithdrawalRequest,
} from "../../../reducers/giftcard.slice";
import { DownloadIcon } from "../../../assests/logo/DownloadIcon";
import { downloadDocument, downloadImagesAsZip } from "../../../utils/util";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import StatusBadge from "../../../components/shared/StatusBadge";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const WITHDRAW_STATUS_LABEL = {
  PENDING: "Pending",
  PENDING_APPROVAL: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  READY_FOR_REDEMPTION: "Ready for Redemption",
  PROCESSING: "Processing",
  ACTION_REQUIRED: "Action Required",
  FAILED: "Failed",
};

const ViewWithdrawRequestPage = ({ dialogMode = false, requestDetail: requestDetailProp, type: typeProp, onUpdated, onInitiatePayment }) => {
  const routeState = useLocation().state || {};
  const requestDetail = requestDetailProp || routeState.requestDetail;
  const type = typeProp || routeState.type;
  const [showLoader, setShowLoader] = useState(false);
  const [openApproveRequest, setOpenApproveRequest] = useState(false);
  const [openRejectRequest, setOpenRejectRequest] = useState(false);
  const [notes, setNotes] = useState("");
  const [paymentDetails, setPaymentDetails] = useState({});
  const [openPayPalPayment, setOpenPayPalPayment] = useState(false);
  const [isPaymentAutomation, setIsPaymentAutomation] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width: 760px)");

  const { showToast } = useContext(AuthContext);

  useEffect(() => {
    if (requestDetail) {
      setPaymentDetails({
        payment_method: requestDetail?.payment_method,
        account_holder_name: requestDetail?.payment_method_details?.account_holder_name,
        phone_number: requestDetail?.payment_method_details?.phone_number || "",
        email: requestDetail?.payment_method_details?.email || requestDetail?.email,
        routing_number: requestDetail?.payment_method_details?.routing_number || "",
        account_number: requestDetail?.payment_method_details?.account_number || "",
        paypal_username: requestDetail?.payment_method_details?.paypal_username || "",
        bank_name: requestDetail?.payment_method_details?.bank_name || "",
        ...requestDetail,
      });
    }
  }, [requestDetail]);

  const handleUpdateGiftCardRedemption = async ({ status }) => {
    setShowLoader(true);

    const message =
      status === "APPROVED"
        ? { success: "Gift Card Request Approved", error: "Error approving Gift Card Request" }
        : status === "REJECTED"
          ? { success: "Gift Card Request Rejected", error: "Error rejecting Gift Card Request" }
          : "Error approving Gift Card Request";

    try {
      const payload = { id: paymentDetails?.id, status };
      if (status === "REJECTED" && notes) payload.notes = notes;

      const response = await dispatch(updateGiftCardRedemptionDetails(payload));
      if (response?.error && Object.keys(response?.error)?.length > 0) {
        setShowLoader(false);
        showToast(response?.error?.message, "error");
        return;
      }
      if (response) {
        showToast(message.success, "success");
        setOpenApproveRequest(false);
        setOpenRejectRequest(false);
        setShowLoader(false);
        if (dialogMode && onUpdated) onUpdated();
        else navigate(-1);
      } else {
        setOpenApproveRequest(false);
        setOpenRejectRequest(false);
        showToast(message.error, "error");
        setShowLoader(false);
      }
    } catch (error) {
      console.error(message, error);
      setOpenApproveRequest(false);
      setOpenRejectRequest(false);
      showToast(message, "error");
      setShowLoader(false);
    }
  };

  const handleDenyPayment = () => {
    setShowLoader(true);
    const payload = { id: paymentDetails?.id, status: "REJECTED", notes };
    if (!notes) delete payload.notes;

    dispatch(updateWithdrawalRequest(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      showToast("Withdrawal request is rejected", "success");
      if (dialogMode && onUpdated) onUpdated();
      else navigate("/dashboard/payouts/withdraw-request");
    });
  };

  const handleDownloadInvoice = async (docType) => {
    setShowLoader(true);
    const payload = { document_type: docType };
    dispatch(downloadWithdrawalInvoice({ id: paymentDetails?.id, payload })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (res?.payload?.length === 0) {
        setShowLoader(false);
        showToast("No files to download");
        return;
      }
      if (res?.payload?.length === 1) {
        const item = res.payload[0];
        downloadDocument(
          item?.document_bytes,
          docType === "INVOICE" ? `${docType + " " + paymentDetails?.id}` : docType,
          item?.content_type
        );
      } else if (res?.payload?.length > 1) {
        downloadImagesAsZip(res?.payload, docType);
      }
      showToast("File downloaded successfully", "success");
      setShowLoader(false);
    });
  };

  const handleMakePayPalPayment = (withdrawal_ids) => {
    setShowLoader(true);
    dispatch(makePayPalPayment({ withdrawal_ids })).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      showToast("Payment initiated successfully.", "success");
      if (dialogMode && onUpdated) onUpdated();
      else navigate("/dashboard/payouts/withdraw-request");
    });
  };

  const columns =
    type !== "withdraw"
      ? [
          { key: "id", label: "ID", show: true },
          { key: "fullName", label: "Member Name", show: true },
          { key: "", label: "Card Vendor", show: true },
          { key: "", label: "Denomination", show: true },
          { key: "", label: "Quantity", show: true },
          { key: "status", label: "Payment Status", show: true },
          { key: "actual_amount", label: "Total Amount", show: true },
        ]
      : [
          { key: "id", label: "ID", show: true },
          { key: "fullName", label: "Member Name", show: true },
          { key: "paymentMethod", label: "Payment Method", show: true },
          { key: "invoice_number", label: "Invoice Number", show: true },
          { key: "account_holder_name", label: "Account Holder Name", show: requestDetail?.paymentMethod === "ACH" },
          { key: "bank_name", label: "Bank Name", show: requestDetail?.paymentMethod === "ACH" },
          { key: "routing_number", label: "Routing Number", show: requestDetail?.paymentMethod === "ACH" },
          { key: "account_number", label: "Account Number", show: requestDetail?.paymentMethod === "ACH" },
          { key: "paypal_username", label: "PayPal Username", show: requestDetail?.paymentMethod === "PayPal" },
          { key: "email", label: "Email", show: true },
          { key: "phone_number", label: "Phone Number", show: !!paymentDetails?.phone_number },
          {
            key: "payment_ref_id",
            label: "Payment Reference ID",
            show: paymentDetails?.status === "APPROVED" || paymentDetails?.status === "REJECTED",
          },
          {
            key: "paymentDate",
            label: "Payment Date",
            show: paymentDetails?.status === "APPROVED" || paymentDetails?.status === "REJECTED",
          },
          { key: "status", label: "Payment Status", show: true },
          {
            key: "notes",
            label: "Notes",
            show: paymentDetails?.status === "APPROVED" || paymentDetails?.status === "REJECTED",
          },
          { key: "actual_amount", label: "Amount", show: true },
        ];

  const visibleColumns = columns.filter((c) => c.show);

  const getValue = (item) => {
    if (item.label === "Card Vendor") {
      const retailer = paymentDetails?.gift_card_details?.retailer || "NA";
      return retailer
        .toString()
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
    }
    if (item.label === "Quantity") return "1";
    return paymentDetails[item.key] || "NA";
  };

  const canActOnRequest =
    paymentDetails?.status === "PENDING_APPROVAL" ||
    paymentDetails?.status === "PENDING" ||
    paymentDetails?.status === "ACTION_REQUIRED";

  return (
    <>
      {showLoader && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Loading...
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: dialogMode ? 0 : "24px 16px" }}>
        <div
          style={{
            width: "100%",
            maxWidth: 760,
            padding: dialogMode ? 0 : "24px",
            backgroundColor: dialogMode ? "transparent" : "#101010",
            borderRadius: dialogMode ? 0 : "10px",
            border: dialogMode ? "none" : "1px solid rgba(255,255,255,0.12)",
            marginBottom: dialogMode ? 0 : "3rem",
            marginTop: dialogMode ? 0 : "3rem",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {!dialogMode && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#c3c8cc",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              <ArrowLeft size={16} /> Back
            </button>
          )}

          {/* Title + download */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <h2 style={{ fontSize: "1.7rem", color: "white", fontWeight: 700, margin: 0 }}>
              {type === "withdraw" ? "Payment Details" : "Gift Card Details"}
            </h2>
            {type === "withdraw" && (
              <button
                type="button"
                onClick={() => handleDownloadInvoice("INVOICE")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#00ADB5",
                  fontSize: 14,
                }}
              >
                <DownloadIcon color="#00ADB5" width="16" />
                Download Invoice
              </button>
            )}
          </div>

          {/* Summary grid */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "8px 16px" }}>
            {visibleColumns.map((item, index) => (
              <React.Fragment key={index}>
                <span style={{ fontSize: 16, color: "gray", alignSelf: "center" }}>
                  {item.label}
                </span>
                <div style={{ alignSelf: "center" }}>
                  {item.label === "Payment Status" ? (
                    <StatusBadge
                      status={paymentDetails?.status}
                      label={WITHDRAW_STATUS_LABEL[paymentDetails?.status]}
                    />
                  ) : (
                    <span
                      style={{
                        fontSize: item.label === "Amount" || item.label === "Total Amount" ? 22 : 15,
                        color: "white",
                        textTransform: item.key === "email" ? "none" : "capitalize",
                      }}
                    >
                      {getValue(item)}
                    </span>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Action buttons */}
          {canActOnRequest && (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
              {paymentDetails?.paymentMethod === "PayPal" && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#c3c8cc",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isPaymentAutomation}
                    onChange={(e) => setIsPaymentAutomation(e.target.checked)}
                    style={{ accentColor: "#00ADB5", cursor: "pointer" }}
                  />
                  Payment Automation
                </label>
              )}

              <Button variant="outline" onClick={() => setOpenRejectRequest(true)}>
                Reject
              </Button>

              {type === "withdraw" ? (
                <Button
                  onClick={() => {
                    if (paymentDetails?.paymentMethod === "PayPal" && isPaymentAutomation) {
                      setOpenPayPalPayment(true);
                    } else {
                      if (dialogMode && onInitiatePayment) onInitiatePayment(paymentDetails);
                      else {
                        navigate("/dashboard/payouts/update-details", {
                          state: { paymentDetails },
                        });
                      }
                    }
                  }}
                >
                  Initiate Payment
                </Button>
              ) : (
                <Button onClick={() => setOpenApproveRequest(true)}>Approve</Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirm PayPal Dialog */}
      <Dialog open={openPayPalPayment} onOpenChange={setOpenPayPalPayment}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Confirm PayPal Withdrawal</DialogTitle>
            <DialogDescription>
              Are you sure you want to proceed for this withdrawal using PayPal? It is basically a
              confirmation dialog box.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPayPalPayment(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleMakePayPalPayment([paymentDetails?.id])}>
              Confirm Withdrawal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Gift Card Dialog */}
      <Dialog open={openApproveRequest} onOpenChange={setOpenApproveRequest}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Approve Gift Card request?</DialogTitle>
            <DialogDescription>
              You are about to confirm the following request. Once confirmed, the member will be
              notified.
            </DialogDescription>
          </DialogHeader>

          <div
            style={{
              marginTop: 8,
              backgroundColor: "rgba(16,16,16,1)",
              padding: 16,
              borderRadius: 8,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px 16px",
            }}
          >
            <span style={{ fontSize: 16, color: "gray" }}>Card Vendor</span>
            <span style={{ fontSize: 15, color: "white" }}>
              {paymentDetails?.gift_card_details?.retailer || "NA"}
            </span>

            <span style={{ fontSize: 16, color: "gray" }}>Denomination</span>
            <span style={{ fontSize: 15, color: "white" }}>
              {paymentDetails?.actual_amount || "NA"}
            </span>

            <span style={{ fontSize: 16, color: "gray" }}>Qty</span>
            <span style={{ fontSize: 15, color: "white" }}>1</span>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenApproveRequest(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleUpdateGiftCardRedemption({ status: "APPROVED" })}>
              Approve Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={openRejectRequest} onOpenChange={setOpenRejectRequest}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Are you sure you want to Deny this request?</DialogTitle>
            <DialogDescription>
              This action will notify the member of your decision. You can provide a reason in the
              notes section below to help them understand the denial.
            </DialogDescription>
          </DialogHeader>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Label>Reason</Label>
            <Textarea
              value={notes}
              placeholder="Provide a reason here"
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRejectRequest(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                type === "withdraw"
                  ? handleDenyPayment()
                  : handleUpdateGiftCardRedemption({ status: "REJECTED" })
              }
            >
              Deny
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewWithdrawRequestPage;
